import { z } from 'zod';
import { PhaseSchema } from './template.js';
// ─── Request schemas ─────────────────────────────────────────
const UsageSchema = z.object({
    tokens: z.object({
        input: z.number().min(0),
        output: z.number().min(0),
        total: z.number().min(0),
    }),
    costUSD: z.number().min(0),
    timeMinutes: z.number().min(0),
});
const AgentCompleteSchema = z.object({
    type: z.literal('agent-complete'),
    runnerId: z.string().min(1),
    phase: PhaseSchema,
    agentId: z.string().min(1),
    runId: z.string().min(1),
    sessionId: z.string().max(200).optional(),
    output: z.string().max(51200).optional(), // up to ~50KB
    usage: UsageSchema.optional(),
    results: z.object({
        summary: z.string().max(5000).optional(),
        priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
        estimatedHours: z.number().min(0).max(1000).optional(),
        approved: z.boolean().optional(),
        testsPassing: z.boolean().optional(),
        inferredTitle: z.string().max(200).optional(),
        inferredTags: z.array(z.string().max(50)).max(20).optional(),
        questions: z.array(z.object({
            question: z.string().min(1).max(2000),
            context: z.string().max(5000).optional(),
        })).optional(),
        discoveredIssues: z.array(z.object({
            title: z.string().min(1).max(200),
            description: z.string().max(5000).optional(),
        })).optional(),
    }).optional(),
    git: z.object({
        branch: z.string().min(1).max(255),
        hasNewCommits: z.boolean(),
    }).optional(),
});
const AgentFailedSchema = z.object({
    type: z.literal('agent-failed'),
    runnerId: z.string().min(1),
    phase: PhaseSchema,
    agentId: z.string().min(1),
    runId: z.string().min(1),
    error: z.object({
        type: z.enum(['failed', 'timeout']),
        message: z.string().max(2000),
    }),
    usage: UsageSchema.optional(),
});
const SystemActionSchema = z.object({
    type: z.literal('system-action'),
    runnerId: z.string().min(1),
    phase: PhaseSchema,
    systemAction: z.object({
        action: z.enum(['auto-merge', 'deployment-status-change']),
        deploymentStatus: z.enum(['pending', 'deploying', 'deployed', 'failed']).optional(),
        failedWorkflows: z.array(z.string()).optional(),
    }),
});
export const PhaseCompleteInputSchema = z.discriminatedUnion('type', [
    AgentCompleteSchema,
    AgentFailedSchema,
    SystemActionSchema,
]);
// ─── Response schema ─────────────────────────────────────────
export const PhaseCompleteResponseSchema = z.object({
    success: z.boolean(),
    advanced: z.boolean().optional(),
    prCreated: z.boolean().optional(),
    prNumber: z.number().optional(),
    prUrl: z.string().optional(),
    questionsPosted: z.number().optional(),
    issuesFiled: z.number().optional(),
    rebaseNeeded: z.boolean().optional(),
    error: z.string().optional(),
});
// ─── Utilities (ported from runner) ──────────────────────────
const PLAN_MIN_LENGTH = 100;
const PLAN_MAX_LENGTH = 50000;
/**
 * Extract the plan markdown from agent output.
 * Tries ```plan fence first, falls back to full narrative (output minus JSON blocks).
 * Returns null if content is too short (<PLAN_MIN_LENGTH chars).
 */
export function extractPlanBlock(output) {
    // Try ```plan fenced block first.
    //
    // Depth tracking alone is insufficient because agents sometimes use bare ``` fences
    // (no language tag) for code examples inside the plan. A bare ``` looks identical to
    // the closing plan fence, so naive depth-- would terminate the plan early.
    //
    // Strategy: track language-tagged nested fences (``` + non-space char) via tagDepth.
    // Any bare ``` not accounted for by tagDepth is either an open/close of a bare nested
    // code block OR the final plan close. Since bare nested fences always come in pairs,
    // the LAST unmatched bare ``` is the actual plan close.
    const lines = output.split('\n');
    let planStart = -1;
    let tagDepth = 0;
    const potentialPlanEnds = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trimEnd();
        if (planStart === -1) {
            if (/^```plan\s*$/.test(line)) {
                planStart = i + 1;
            }
        }
        else {
            if (/^```\S/.test(line)) {
                // Opening a language-tagged nested fence (e.g. ```typescript)
                tagDepth++;
            }
            else if (/^```\s*$/.test(line)) {
                if (tagDepth > 0) {
                    // Closing a language-tagged nested fence
                    tagDepth--;
                }
                else {
                    // Bare ``` at plan level — could be open/close of a bare code block or the plan close.
                    // Collect all of them; we'll take the last one as the actual plan close.
                    potentialPlanEnds.push(i);
                }
            }
        }
    }
    if (planStart !== -1 && potentialPlanEnds.length > 0) {
        const planEnd = potentialPlanEnds[potentialPlanEnds.length - 1];
        const plan = lines.slice(planStart, planEnd).join('\n').trim();
        return plan.length >= PLAN_MIN_LENGTH ? plan.slice(0, PLAN_MAX_LENGTH) : null;
    }
    // Fallback: full narrative minus JSON blocks
    const narrative = output.replace(/```json\s*\n[\s\S]*?```/g, '').trim();
    return narrative.length >= PLAN_MIN_LENGTH ? narrative.slice(0, PLAN_MAX_LENGTH) : null;
}
/** Normalize a title for fuzzy comparison: lowercase, strip punctuation, collapse whitespace. */
export function normalizeTitle(title) {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Find an existing card whose title is similar enough to be a duplicate.
 * Uses word-overlap ratio — if 60%+ of words match, it's considered a dup.
 */
export function findSimilarCard(issueTitle, existingCards) {
    const normalizedIssue = normalizeTitle(issueTitle);
    const issueWords = new Set(normalizedIssue.split(' ').filter(w => w.length > 2));
    if (issueWords.size === 0)
        return undefined;
    let bestMatch;
    let bestScore = 0;
    for (const card of existingCards) {
        const normalizedCard = normalizeTitle(card.title);
        // Exact normalized match
        if (normalizedCard === normalizedIssue)
            return card;
        // Word overlap
        const cardWords = new Set(normalizedCard.split(' ').filter(w => w.length > 2));
        if (cardWords.size === 0)
            continue;
        let overlap = 0;
        for (const word of issueWords) {
            if (cardWords.has(word))
                overlap++;
        }
        // Score = overlap relative to the smaller set
        const score = overlap / Math.min(issueWords.size, cardWords.size);
        if (score > bestScore) {
            bestScore = score;
            bestMatch = card;
        }
    }
    return bestScore >= 0.6 ? bestMatch : undefined;
}
//# sourceMappingURL=phase-complete.js.map